import React, { useEffect, useState } from 'react'
import axiosInstance, { API_BASE } from '../apiconfig'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid'
import Select from 'react-select'
import CommonFilter from '../components/CommonFilter'   // ✅ SHARED FILTER

const API = `${API_BASE}/api/NagadAmount`
const FARMERS_API = `${API_BASE}/api/farmers/GetFarmers`

const TransactionTypes = [
  { value: 0, label: "Loan(Diye)" },
  { value: 1, label: "Deposit(Jama)" }
]

export default function NagadAmount() {

  const [farmers, setFarmers] = useState([])
  const [editing, setEditing] = useState(null)
  const [open, setOpen] = useState(false)

  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)

  const [filterFarmerId, setFilterFarmerId] = useState(0)
  const [monthYear, setMonthYear] = useState(
    new Date().toISOString().slice(0, 7)
  )

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  })

  const [form, setForm] = useState({
    userID: 0,
    toPerson: '',
    reason: '',
    amount: 0,
    createdDate: new Date().toISOString().slice(0, 10),
    types: 0
  })

  /* ================= FARMER OPTIONS ================= */
  const farmerOptions = farmers.map(f => ({
    value: f.id,
    label: `(${f.code}) ${f.firstName} ${f.lastName}`
  }))

  /* ================= TABLE COLUMNS ================= */
  const columns = [
    { field: "id", headerName: "ID", width: 80 },

    {
      field: "farmerName",
      headerName: "Farmer",
      flex: 1,
      minWidth: 220,
      renderCell: (params) =>
        `(${params.row?.farmer?.code}) ${params.row?.farmer?.firstName || ""} ${params.row?.farmer?.lastName || ""}`
    },

    { field: "toPerson", headerName: "To Person", width: 160 },
    { field: "amount", headerName: "Amount", width: 120 },

    {
      field: "types",
      headerName: "Type",
      width: 120,
      renderCell: (params) =>
        TransactionTypes.find(t => t.value === params.value)?.label
    },

    {
      field: "createdDate",
      headerName: "Date",
      width: 130,
      renderCell: (params) => {
        if (!params.value) return "";
        const d = new Date(params.value);

        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();

        return `${day}/${month}/${year}`;
      }
    },

    {
      field: "actions",
      type: "actions",
      width: 120,
      getActions: (params) => [
        <GridActionsCellItem
          key={`edit-${params.id}`}
          icon={<EditIcon />}
          label="Edit"
          onClick={() => startEdit(params.row)}
        />,
        <GridActionsCellItem
          key={`delete-${params.id}`}
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => del(params.row.id)}
        />,
      ],
    },
  ]

  /* ================= EFFECTS ================= */
  useEffect(() => {
    fetchFarmers()
  }, [])

  useEffect(() => {
    fetchList()
  }, [paginationModel.page, paginationModel.pageSize, monthYear, filterFarmerId])

  /* ================= API ================= */
  const fetchFarmers = async () => {
    const res = await axiosInstance.get(FARMERS_API)
    setFarmers(res?.data ?? [])
  }

  const fetchList = async () => {
    setLoading(true)

    const res = await axiosInstance.get(API, {
      params: {
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        dateTime: new Date(`${monthYear}-01`),
        filterFarmerId: filterFarmerId || 0
      },
    })

    setRows(res?.data?.data ?? [])
    setTotal(res?.data?.totalCount ?? 0)
    setLoading(false)
  }

  /* ================= FORM ================= */
  const submit = async (e) => {
    e.preventDefault()

    const payload = {
      userID: form.userID,
      toPerson: form.toPerson,
      reason: form.reason,
      amount: parseFloat(form.amount),
      createdDate: form.createdDate,
      types: form.types
    }

    if (editing) {
      await axiosInstance.put(`${API}/${editing}`, payload)
    } else {
      await axiosInstance.post(API, payload)
    }

    resetForm()
    fetchList()
  }

  const startEdit = (r) => {
    setEditing(r.id)
    setOpen(true)
    setForm({
      userID: r.userID,
      toPerson: r.toPerson,
      reason: r.reason ?? '',
      amount: r.amount,
      createdDate: r.createdDate
        ? r.createdDate.split('T')[0]
        : new Date().toISOString().slice(0, 10),
      types: r.types
    })
  }

  const del = async (id) => {
    if (!confirm('You want to Delete?')) return
    await axiosInstance.delete(`${API}/${id}`)
    fetchList()
  }

  const resetForm = () => {
    setEditing(null)
    setForm({
      userID: 0,
      toPerson: '',
      reason: '',
      amount: 0,
      createdDate: new Date().toISOString().slice(0, 10),
      types: 0
    })
  }

  /* ================= UI ================= */
  return (
    <div>

      {/* ================= ADD / EDIT ================= */}
      <div className="panel">
        <div className="panel-header" onClick={() => setOpen(!open)}>
          <h3>Add / Edit Nagad Amount</h3>
          <span className={open ? "rotate" : ""}>▼</span>
        </div>

        <div className={`collapse ${open ? "open" : ""}`}>
          <div className="panel-body">
            <form onSubmit={submit} className="form">

              <label>Farmer
                <Select
                  classNamePrefix="react-select"
                  placeholder="Search Farmer"
                  isClearable
                  isSearchable
                  options={farmerOptions}
                  value={farmerOptions.find(o => o.value === form.userID) || null}
                  onChange={(opt) =>
                    setForm({ ...form, userID: opt ? opt.value : 0 })
                  }
                />
              </label>

              <label>To Person
                <input
                  value={form.toPerson}
                  onChange={e => setForm({ ...form, toPerson: e.target.value })}
                />
              </label>

              <label>Reason
                <input
                  value={form.reason}
                  onChange={e => setForm({ ...form, reason: e.target.value })}
                />
              </label>

              <label>Amount
                <input
                  type="number"
                  value={form.amount}
                  onChange={e => setForm({ ...form, amount: e.target.value })}
                />
              </label>

              <label>Transaction Type
                <select
                  value={form.types}
                  onChange={e =>
                    setForm({ ...form, types: parseInt(e.target.value) })
                  }
                >
                  {TransactionTypes.map(t =>
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  )}
                </select>
              </label>

              <label>Date
                <input
                  type="date"
                  value={form.createdDate}
                  onChange={e =>
                    setForm({ ...form, createdDate: e.target.value })
                  }
                />
              </label>

              <div className="actions">
                <button type="submit">
                  {editing ? 'Update' : 'Create'}
                </button>
                {editing && (
                  <button type="button" onClick={resetForm}>
                    Cancel
                  </button>
                )}
              </div>

            </form>
          </div>
        </div>
      </div>

      {/* ================= SHARED FILTER ================= */}
      <CommonFilter
        loading={loading}
        filters={[
          {
            key: "farmer",
            type: "select",
            placeholder: "Search Farmer",
            options: farmerOptions,
            value: filterFarmerId,
            onChange: setFilterFarmerId,
          },
          {
            key: "month",
            type: "month",
            value: monthYear,
            onChange: setMonthYear,
          },
        ]}
        onClear={() => {
          setFilterFarmerId(0)
          setMonthYear(new Date().toISOString().slice(0, 7))
        }}
      />

      {/* ================= TABLE ================= */}
      <div className="data">
        <DataGrid
          rows={rows}
          columns={columns}
          rowCount={total}
          loading={loading}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50, 100]}
          columnVisibilityModel={{ id: false }}

        />
      </div>

    </div>
  )
}
